import React, { useEffect } from 'react';
import {
  Edit,
  TextInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SelectArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  BooleanInput,
  required,
  email,
  SaveButton,
  TopToolbar,
  useRecordContext,
  useNotify,
  useRedirect,
  DeleteWithConfirmButton,
  AutocompleteInput,
  useEditContext,
  Toolbar,
  TabbedForm,
  FormTab,
} from 'react-admin';
import { SectionedForm, SectionedFormLayout } from '../../components/layouts';
import { usePermissions } from '../../auth/PermissionsContext';
import { useFormContext } from 'react-hook-form';
import { PROVIDER_ROLES_RESOURCE_NAME as PROVIDER_ROLES_RESOURCE } from '../../roles/provider.roles.dataProvider';
import { NON_PROVIDER_ROLES_RESOURCE_NAME as NON_PROVIDER_ROLES_RESOURCE } from '../../roles/non.provider.roles.dataProvider';
import { ALL_ORGANIZATIONS_RESOURCE_NAME as ORGANIZATIONS_RESOURCE } from '../../organizations/organizations.dataProvider';
import { US_STATES_RESOURCE_NAME as US_STATES_RESOURCE } from '../../usStates/usStates.dataProvider';
import { LANGUAGES_RESOURCE_NAME as LANGUAGES_RESOURCE } from '../../languages/languages.dataProvider';
import { CODES_RESOURCE_NAME as CODES_RESOURCE } from '../../codes/codes.dataProvider';
import { userFields } from '../user.model';
import { rolesFields } from '../../roles/roles.model';
import { userRelativeFields } from '../userRelative.model';
import { codesFields } from '../../codes/codes.model';
import CustomDatePicker from '../../app/customDatePicker';
import { validatePhone } from '../../utils/validation';
import {
  prenatalInfoFields,
  PrenatalInfoTypeFourChoices,
  PrenatalInfoTypeThreeChoices,
} from '../userFormConfig';
import { PERMISSION_TEMPLATES_RESOURCE_NAME as PERMISSION_TEMPLATE_RESOURCE } from '../../permissionTemplates/permission.template.dataProvider';
import { ConditionalField } from '../../utils/conditionalField';
import { optionalField } from '../../utils/constants';
import { useLocation } from 'react-router-dom';
import { useEditWithRefresh } from '../../hooks/useMutationWithRefresh';
import PermissionsTab from './PermissionsTab.component';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FeatureFlags } from '../../constants/featureFlags';

const UserTitle = () => {
  const record = useRecordContext();
  const { isLoading } = useEditContext();

  if (isLoading) return 'Loading...';
  return record
    ? `${record[userFields.firstName]} ${record[userFields.lastName]}`
    : '';
};

const validateEmail = [required(), email()];
const validateRequired = [required()];
const preserveIdentity = (value) => value;

const OrgCodeField = () => {
  const record = useRecordContext();
  const { getValues, setValue } = useFormContext();
  const values = getValues();

  const isOrgForm =
    typeof values?.org_id !== 'undefined' &&
    typeof record?.org_id !== 'undefined';

  useEffect(() => {
    if (isOrgForm) {
      if (values.org_id === record.org_id) {
        setValue(
          'code_id',
          record.org_codes?.length > 0 ? record.org_codes[0].id : null,
        );
      } else setValue('code_id', null);
    }
  }, [values?.org_id, record?.org_id, record?.org_codes, isOrgForm, setValue]);

  return (
    <ReferenceInput
      source={userFields.codeId}
      reference={CODES_RESOURCE}
      perPage={1000}
      sort={{ field: codesFields.codeName, order: 'ASC' }}
      filter={{ organization_id: values?.org_id }}
      label="Code"
      defaultValue={
        record?.org_codes?.length > 0 ? record.org_codes[0].id : null
      }
      allowEmpty
      sx={{ width: 400 }}
    >
      <AutocompleteInput
        optionText={codesFields.codeName}
        sx={{ width: 400 }}
      />
    </ReferenceInput>
  );
};

// Conditional field components for different user types
const ProviderFields = () => {
  const { record } = useEditContext();
  const isProvider = record && record.isProvider;

  if (!isProvider) return null;

  return (
    <>
      <ReferenceArrayInput
        source={userFields.providerRoleIds}
        perPage={10000}
        sort={{ field: 'name', order: 'ASC' }}
        reference={PROVIDER_ROLES_RESOURCE}
        label="Provider Roles"
        allowEmpty
        sx={{ width: 400 }}
      >
        <SelectArrayInput
          optionText={rolesFields.roleName}
          sx={{ width: 400 }}
        />
      </ReferenceArrayInput>
      <TextInput
        source={userFields.providerBio}
        multiline
        rows={5}
        resettable
        sx={{ width: 400 }}
      />
      <TextInput
        source={userFields.providerBioEs}
        label="Provider Bio (Spanish)"
        multiline
        rows={5}
        resettable
        sx={{ width: 400 }}
      />
    </>
  );
};

const PatientFields = () => {
  const { record } = useEditContext();
  const isPatient = record && record.isPatient;

  if (!isPatient) return null;

  return (
    <>
      <CustomDatePicker
        source={userFields.dob}
        label="DOB"
        sx={{ width: 400 }}
      />
      <CustomDatePicker
        source={userFields.dueDate}
        label="Due date"
        sx={{ width: 400 }}
      />
      <ReferenceInput
        source={userFields.orgId}
        reference={ORGANIZATIONS_RESOURCE}
        perPage={10000}
        sort={{ field: 'name', order: 'ASC' }}
        label="Organization"
        allowEmpty
        sx={{ width: 400 }}
      >
        <SelectInput optionText="name" sx={{ width: 400 }} />
      </ReferenceInput>
      <TextInput
        source={userFields.wicId}
        label="WIC ID"
        parse={preserveIdentity}
        sx={{ width: 400 }}
      />
      <SelectInput
        source={userFields.birthWish}
        label={prenatalInfoFields.birthWish}
        choices={PrenatalInfoTypeFourChoices}
        sx={{ width: 400 }}
      />
      <SelectInput
        source={userFields.birthPartner}
        label={prenatalInfoFields.birthPartner}
        choices={PrenatalInfoTypeThreeChoices}
        sx={{ width: 400 }}
      />
      <SelectInput
        source={userFields.prenatalVisit}
        label={prenatalInfoFields.prenatalVisit}
        choices={PrenatalInfoTypeFourChoices}
        sx={{ width: 400 }}
      />
    </>
  );
};

const ProviderAdminFields = () => {
  const { record } = useEditContext();
  const isProviderOrAdmin = record && (record.isProvider || record.isAdmin);

  if (!isProviderOrAdmin) return null;

  return (
    <>
      <ReferenceInput
        source={userFields.permissionTemplateId}
        reference={PERMISSION_TEMPLATE_RESOURCE}
        sx={{ width: 400 }}
      >
        <SelectInput
          optionText="name"
          validate={validateRequired}
          sx={{ width: 400 }}
        />
      </ReferenceInput>
    </>
  );
};

const AdminFields = () => {
  const { record } = useEditContext();
  const isAdmin = record && record.isAdmin;

  if (!isAdmin) return null;

  return (
    <>
      <BooleanInput
        source={userFields.isMfaRequired}
        label="MFA Required"
        sx={{ width: 400 }}
      />
    </>
  );
};

const NonAdminFields = () => {
  const { record } = useEditContext();
  const isAdmin = record && record.isAdmin;

  if (isAdmin) return null;

  return (
    <>
      <ReferenceInput
        source={userFields.usState}
        reference={US_STATES_RESOURCE}
        label="State"
        sx={{ width: 400 }}
      >
        <SelectInput
          optionText="name"
          label="State"
          validate={[record && record.isPatient ? optionalField : required()]}
          sx={{ width: 400 }}
        />
      </ReferenceInput>
    </>
  );
};

const ProviderOnlyFields = () => {
  const { record } = useEditContext();
  const isProvider = record && record.isProvider;

  if (!isProvider) return null;

  return (
    <>
      <TextInput
        source={userFields.zipCode}
        label="ZIP code"
        parse={preserveIdentity}
        sx={{ width: 400 }}
      />
    </>
  );
};

const ConditionalRoleFormFields = () => {
  const { record } = useEditContext();
  if (!record) return [];

  const fields = [];

  // Provider/Admin fields (only if they have or will have Healthie membership)
  // Template permissions require Healthie organization membership
  if (record.isProvider || (record.isAdmin && record.healthieId)) {
    fields.push(
      <ReferenceInput
        key="permissionTemplate"
        source={userFields.permissionTemplateId}
        reference={PERMISSION_TEMPLATE_RESOURCE}
        label="Healthie Permission Template"
      >
        <SelectInput
          optionText="name"
          validate={validateRequired}
        />
      </ReferenceInput>
    );
  }
  
  // Show info message for admins without Healthie ID
  if (record.isAdmin && !record.healthieId && !record.isProvider) {
    fields.push(
      <div key="healthie-info" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <strong>Note:</strong> Template permissions require Healthie organization membership. 
        Set a template permission during creation to automatically create Healthie membership.
      </div>
    );
  }

  // Provider-only fields
  if (record.isProvider) {
    fields.push(
      <ReferenceArrayInput
        key="providerRoles"
        source={userFields.providerRoleIds}
        perPage={10000}
        sort={{ field: 'name', order: 'ASC' }}
        reference={PROVIDER_ROLES_RESOURCE}
        label="Provider role"
        allowEmpty
      >
        <SelectArrayInput
          optionText={rolesFields.roleName}
        />
      </ReferenceArrayInput>,
      <TextInput
        key="providerBio"
        source={userFields.providerBio}
        label="Provider bio"
        multiline
        rows={5}
        resettable
      />,
      <TextInput
        key="providerBioEs"
        source={userFields.providerBioEs}
        label="Provider Bio (Spanish)"
        multiline
        rows={5}
        resettable
      />,
      <TextInput
        key="zipCode"
        source={userFields.zipCode}
        label="ZIP code"
        parse={preserveIdentity}
      />
    );
  }

  // Patient-only fields
  if (record.isPatient) {
    fields.push(
      <CustomDatePicker
        key="dob"
        source={userFields.dob}
        label="DOB"
      />,
      <CustomDatePicker
        key="dueDate"
        source={userFields.dueDate}
        label="Due date"
      />,
      <ReferenceInput
        key="organization"
        source={userFields.orgId}
        reference={ORGANIZATIONS_RESOURCE}
        perPage={10000}
        sort={{ field: 'name', order: 'ASC' }}
        label="Organization"
        allowEmpty
      >
        <SelectInput optionText="name" />
      </ReferenceInput>,
      <TextInput
        key="wicId"
        source={userFields.wicId}
        label="WIC ID"
        parse={preserveIdentity}
      />,
      <SelectInput
        key="birthWish"
        source={userFields.birthWish}
        label={prenatalInfoFields.birthWish}
        choices={PrenatalInfoTypeFourChoices}
      />,
      <SelectInput
        key="birthPartner"
        source={userFields.birthPartner}
        label={prenatalInfoFields.birthPartner}
        choices={PrenatalInfoTypeThreeChoices}
      />,
      <SelectInput
        key="prenatalVisit"
        source={userFields.prenatalVisit}
        label={prenatalInfoFields.prenatalVisit}
        choices={PrenatalInfoTypeFourChoices}
      />
    );
  }

  // Admin-only fields
  if (record.isAdmin) {
    fields.push(
      <BooleanInput
        key="mfaRequired"
        source={userFields.isMfaRequired}
        label="MFA Required"
      />
    );
  }

  // Non-admin fields (for patients and providers)
  if (!record.isAdmin) {
    fields.push(
      <ReferenceInput
        key="state"
        source={userFields.usState}
        reference={US_STATES_RESOURCE}
        label="State"
      >
        <SelectInput
          optionText="name"
          label="State"
          validate={[record.isPatient ? optionalField : required()]}
        />
      </ReferenceInput>
    );
  }

  return fields;
};

const UserEditForm = () => {
  const { record, isLoading } = useEditContext();
  const isRBACEnabled = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_RBAC);
  const { hasPermission } = usePermissions();
  
  if (isLoading) return null;

  const { isProvider, isPatient, isAdmin } = record;
  
  // Show Permissions tab if logged-in user has permission to manage roles/permissions
  const canManagePermissions = hasPermission('admin:manage-roles-and-permissions');
  
  const customToolbar = (
    <Toolbar>
      <SaveButton label="SAVE" />
      <DeleteWithConfirmButton
        label="DELETE"
        confirmTitle="Delete User"
        confirmContent="Are you sure you want to delete this user? This action is permanent and cannot be undone."
      />
    </Toolbar>
  );

  const sections = [
    {
      title: "Basic Information",
      fields: [
        <ReferenceInput
          key="role"
          source={userFields.roleId}
          reference={NON_PROVIDER_ROLES_RESOURCE}
          perPage={10000}
          sort={{ field: 'name', order: 'ASC' }}
          label="Role"
          allowEmpty
        >
          <SelectInput
            optionText="name"
            validate={validateRequired}
            readOnly
          />
        </ReferenceInput>,
        <TextInput
          key="firstName"
          source={userFields.firstName}
          validate={validateRequired}
          label="First Name"
        />,
        <TextInput
          key="lastName"
          source={userFields.lastName}
          label="Last Name"
          parse={preserveIdentity}
        />,
        <TextInput
          key="email"
          source={userFields.email}
          validate={validateEmail}
          label="Email"
        />,
        <TextInput
          key="phoneNumber"
          source={userFields.phoneNumber}
          validate={[isProvider ? required() : optionalField, validatePhone()]}
          label="Phone"
          parse={preserveIdentity}
        />,
        <ReferenceInput
          key="language"
          source={userFields.lang}
          reference={LANGUAGES_RESOURCE}
          label="Language"
          allowEmpty
        >
          <SelectInput label="Language" optionText="name" />
        </ReferenceInput>
      ]
    },
    {
      title: "Role-Specific Information",
      fields: ConditionalRoleFormFields()
    },
    {
      title: "Organization & Settings",
      fields: [
        <OrgCodeField key="orgCode" />,
        <BooleanInput
          key="isActive"
          source={userFields.isActive}
          label="Is active"
        />
      ]
    }
  ];

  // If logged-in user cannot manage permissions or RBAC disabled, use SectionedForm only
  if (!canManagePermissions || !isRBACEnabled) {
    return (
      <SectionedForm
        sections={sections}
        toolbar={customToolbar}
        sanitizeEmptyValues
      />
    );
  }

  // If admin and RBAC enabled, use TabbedForm with Details + Permissions
  return (
    <TabbedForm
      sanitizeEmptyValues
      toolbar={customToolbar}
      syncWithLocation={false}
    >
      <FormTab label="Details" path="details">
        <SectionedFormLayout sections={sections} />
      </FormTab>
      <FormTab label="Permissions" path="permissions">
        <PermissionsTab />
      </FormTab>
    </TabbedForm>
  );
};

const UserEdit = () => {
  const location = useLocation();
  const redirect = useRedirect();

  const mutationOptions = useEditWithRefresh('users', {
    successMessage: 'User updated successfully',
    onSuccessCallback: () => {
      redirect(`${location.pathname}/show`);
    },
    redirectTo: null, // Disable default redirect since we're using custom logic
  });

  return (
    <Edit
      title={<UserTitle />}
      mutationMode="pessimistic"
      mutationOptions={mutationOptions}
    >
      <UserEditForm />
    </Edit>
  );
};

export default UserEdit;
